import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { MaskedNumberInput } from "@/components/ui/masked-input";
import type { MerchantDropdownItem } from "../../../../domain/model/RewardCatalogModel";
import { FetchMerchantsDropdownUseCase } from "../../../../domain/usecase";

export interface RewardCommonFieldsSectionProps {
  readonly type: string;
  readonly pointsAmount: string;
  readonly pointsAmountError?: string;
  readonly quantity: string;
  readonly quantityError?: string;
  readonly quantityDisabled?: boolean;
  readonly validityPeriod: string;
  readonly validityPeriodError?: string;
  readonly validityPeriodDisabled?: boolean;
  readonly merchant: string;
  readonly merchantError?: string;
  readonly merchantDisabled?: boolean;
  readonly startDate: string;
  readonly startDateError?: string;
  readonly endDate: string;
  readonly endDateError?: string;
  readonly onChangePointsAmount: (value: string) => void;
  readonly onChangeQuantity: (value: string) => void;
  readonly onChangeValidityPeriod: (value: string) => void;
  readonly onChangeMerchant: (value: string) => void;
  readonly onChangeStartDate: (value: string) => void;
  readonly onChangeEndDate: (value: string) => void;
}

export const RewardCommonFieldsSection = ({
  pointsAmount,
  pointsAmountError,
  quantity,
  quantityError,
  quantityDisabled = false,
  validityPeriod,
  validityPeriodError,
  validityPeriodDisabled = false,
  merchant,
  merchantError,
  merchantDisabled = false,
  startDate,
  startDateError,
  endDate,
  endDateError,
  onChangePointsAmount,
  onChangeQuantity,
  onChangeValidityPeriod,
  onChangeMerchant,
  onChangeStartDate,
  onChangeEndDate,
}: RewardCommonFieldsSectionProps): React.JSX.Element => {
  const [merchants, setMerchants] = useState<readonly MerchantDropdownItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);

  useEffect(() => {
    const fetchMerchants = async () => {
      try {
        setIsLoading(true);
        setFetchError(null);
        const data = await FetchMerchantsDropdownUseCase();
        setMerchants(data);
      } catch (error) {
        console.error("Failed to fetch merchants:", error);
        setFetchError("Failed to load merchants");
      } finally {
        setIsLoading(false);
      }
    };

    fetchMerchants();
  }, []);

  return (
    <>
      <div className="grid grid-cols-2 gap-4">
        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="pointsAmount"
            className="text-sm font-semibold text-gray-800 text-left"
          >
            Points Amount
          </label>

          <MaskedNumberInput
            id="pointsAmount"
            value={pointsAmount}
            onChange={onChangePointsAmount}
            placeholder="Points Amount"
            errorMessage={pointsAmountError}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="quantity"
            className="text-sm font-semibold text-gray-800 text-left"
          >
            Quantity
          </label>
          <MaskedNumberInput
            id="quantity"
            value={quantity}
            onChange={onChangeQuantity}
            placeholder={quantityDisabled ? "Auto-filled from voucher codes" : "Quantity"}
            errorMessage={quantityError}
            disabled={quantityDisabled}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="validityPeriod"
            className="text-sm font-semibold text-gray-800 text-left"
          >
            Validity Period (Days)
          </label>
          <MaskedNumberInput
            id="validityPeriod"
            value={validityPeriod}
            onChange={onChangeValidityPeriod}
            placeholder={validityPeriodDisabled ? "Auto-calculated from dates" : "Validity Period"}
            errorMessage={validityPeriodError}
            disabled={validityPeriodDisabled}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="merchant"
            className="text-sm font-semibold text-gray-800 text-left"
          >
            Merchant
          </label>
          <Select
            id="merchant"
            value={merchant}
            onChange={(event) => onChangeMerchant(event.target.value)}
            errorMessage={merchantError ?? fetchError ?? undefined}
            disabled={isLoading || merchantDisabled}
          >
            <option value="" disabled>
              {isLoading ? "Loading..." : "Select Merchant"}
            </option>
            {merchants.map((m) => (
              <option key={m.merchantId} value={m.merchantId}>
                {m.merchantName}
              </option>
            ))}
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="startDate"
            className="text-sm font-semibold text-gray-800 text-left"
          >
            Start Date
          </label>
          <Input
            id="startDate"
            type="date"
            value={startDate}
            onChange={(event) => onChangeStartDate(event.target.value)}
            errorMessage={startDateError}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="endDate"
            className="text-sm font-semibold text-gray-800 text-left"
          >
            End Date
          </label>
          <Input
            id="endDate"
            type="date"
            value={endDate}
            onChange={(event) => onChangeEndDate(event.target.value)}
            errorMessage={endDateError}
          />
        </div>
      </div>
    </>
  );
};
